import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Search, Plus, Minus, Trash2, Loader2, CheckCircle2, PackageOpen } from "lucide-react";
import { toast } from "sonner";

const clinicLabel = (c) => {
  const m = (c?.name || "").match(/عيادة\s*(\d+)/);
  return m ? `Clinic ${m[1]}` : c?.name || "";
};

export default function InstrumentsRequestPublic() {
  const { clinicId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [requesterName, setRequesterName] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const loadData = () => axios.get(`${API}/instruments/public/clinic/${clinicId}`)
    .then(r => setData(r.data))
    .catch(() => setError(true))
    .finally(() => setLoading(false));

  useEffect(() => { loadData(); }, [clinicId]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const inCart = new Set(cart.map(c => c.id));
    return data.items.filter(it => !inCart.has(it.id) && (!search || it.name.toLowerCase().includes(search.toLowerCase()))).slice(0, 30);
  }, [data, search, cart]);

  const addItem = (it) => setCart([...cart, { ...it, qty: 1 }]);
  const stepQty = (id, delta) => setCart(cart.map(c => {
    if (c.id !== id) return c;
    const q = Math.min(Math.max((parseInt(c.qty, 10) || 1) + delta, 1), c.available);
    return { ...c, qty: q };
  }));
  const removeItem = (id) => setCart(cart.filter(c => c.id !== id));

  const submit = async () => {
    if (!requesterName.trim()) { toast.error("Enter the requester name"); return; }
    if (cart.length === 0) { toast.error("Add at least one instrument"); return; }
    for (const c of cart) {
      const q = parseInt(c.qty, 10);
      if (isNaN(q) || q < 1) { toast.error(`Invalid quantity for ${c.name}`); return; }
      if (q > c.available) { toast.error(`Only ${c.available} of ${c.name} available`); return; }
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/instruments/public/request`, {
        clinic_id: clinicId,
        requester_name: requesterName,
        notes,
        items: cart.map(c => ({ instrument_id: c.id, qty: parseInt(c.qty, 10) })),
      });
      setDone(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit request");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div dir="ltr" className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-[#655dc6]" /></div>;
  }
  if (error) {
    return (
      <div dir="ltr" className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <p className="text-slate-500">Clinic not found or invalid link</p>
      </div>
    );
  }
  if (done) {
    return (
      <div dir="ltr" className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full border-0 shadow-lg">
          <CardContent className="py-10 text-center space-y-4" data-testid="request-success">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
            <h2 className="text-xl font-bold text-[#655dc6]">Request Submitted</h2>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Pickup PIN — show it at the Sterile Storage</p>
              <div className="inline-block bg-[#F5F3F9] border-2 border-[#655dc6] rounded-xl px-8 py-3" data-testid="request-pin">
                <span className="text-3xl font-mono font-bold text-[#4a3fa8] tracking-[0.4em]">{done.pin}</span>
              </div>
            </div>
            <p className="text-xs text-slate-500">Lost the code? Scan the clinic QR again — pending requests and their PINs are always listed there.</p>
            <Button variant="outline" onClick={() => { setDone(null); setCart([]); setNotes(""); loadData(); }} data-testid="new-request-btn">New Request</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div dir="ltr" className="min-h-screen bg-slate-50 pb-10" data-testid="instruments-request-page">
      <div className="bg-[#655dc6] text-white py-6 px-4 text-center">
        <img src="/lucent-logo.png" alt="LUCENT" className="h-10 mx-auto mb-2 brightness-0 invert" onError={e => { e.target.style.display = "none"; }} />
        <h1 className="text-xl font-bold flex items-center justify-center gap-2"><PackageOpen className="w-6 h-6" /> Instruments Request</h1>
        <p className="text-sm opacity-90 mt-1">{clinicLabel(data.clinic)}</p>
      </div>

      <div className="max-w-lg mx-auto px-4 mt-5 space-y-4">
        {data.pending_requests?.length > 0 && (
          <Card className="border-0 shadow-sm border-l-4 border-l-amber-400">
            <CardContent className="pt-5 space-y-2" data-testid="pending-requests">
              <h3 className="font-semibold text-amber-700 text-sm">Pending Requests — Pickup PINs</h3>
              {data.pending_requests.map(p => (
                <div key={p.id} className="flex items-center justify-between bg-amber-50 rounded-lg p-2.5" data-testid={`pending-req-${p.id}`}>
                  <div className="min-w-0">
                    <p className="text-xs font-medium">{p.requester_name} — {(p.created_at || "").slice(0, 16).replace("T", " ")}</p>
                    <p className="text-[10px] text-slate-500 truncate">{(p.items || []).map(i => `${i.instrument_name} × ${i.qty}`).join(", ")}</p>
                  </div>
                  <span className="font-mono font-bold text-lg text-amber-700 tracking-widest shrink-0 ml-3">{p.pin}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-5 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search instruments to add..." className="pl-10" data-testid="pub-search" />
            </div>
            {search && (
              <div className="max-h-56 overflow-y-auto divide-y border rounded-lg">
                {filtered.map(it => (
                  <button key={it.id} onClick={() => { addItem(it); setSearch(""); }} className="w-full flex items-center justify-between p-2.5 hover:bg-slate-50 text-left" data-testid={`pub-add-${it.id}`}>
                    <span className="text-xs font-medium">{it.name}</span>
                    <span className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">Available {it.available}</Badge>
                      <Plus className="w-4 h-4 text-[#655dc6]" />
                    </span>
                  </button>
                ))}
                {filtered.length === 0 && <p className="text-center text-xs text-slate-400 py-4">No matching available instruments</p>}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-5 space-y-3">
            <h3 className="font-semibold text-[#655dc6] text-sm">Requested Instruments ({cart.length})</h3>
            {cart.length === 0 && <p className="text-center text-xs text-slate-400 py-4">Search and add the instruments you need from the warehouse</p>}
            {cart.map(c => (
              <div key={c.id} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2" data-testid={`cart-item-${c.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{c.name}</p>
                  <p className="text-[10px] text-slate-400">Available in warehouse: {c.available}</p>
                </div>
                <div className="flex items-center gap-1 bg-white border rounded-full px-1 py-0.5 shrink-0">
                  <button
                    onClick={() => stepQty(c.id, -1)}
                    disabled={(parseInt(c.qty, 10) || 1) <= 1}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-[#655dc6] disabled:text-slate-300 active:bg-[#F5F3F9]"
                    data-testid={`cart-minus-${c.id}`}
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-8 text-center font-bold text-sm text-[#4a3fa8]" data-testid={`cart-qty-${c.id}`}>{c.qty}</span>
                  <button
                    onClick={() => stepQty(c.id, 1)}
                    disabled={(parseInt(c.qty, 10) || 1) >= c.available}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-[#655dc6] disabled:text-slate-300 active:bg-[#F5F3F9]"
                    data-testid={`cart-plus-${c.id}`}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <Button size="sm" variant="ghost" onClick={() => removeItem(c.id)} className="text-red-400 h-8 w-8 p-0" data-testid={`cart-del-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-5 space-y-3">
            <div className="space-y-1">
              <Label>Requester name *</Label>
              <Input value={requesterName} onChange={e => setRequesterName(e.target.value)} placeholder="Name" data-testid="pub-requester" />
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" rows={2} data-testid="pub-notes" />
            </div>
            <Button onClick={submit} disabled={submitting} className="w-full bg-[#655dc6] hover:bg-[#5449a8] text-white" data-testid="pub-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Request to Sterile Storage"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
